import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { OrganizationalStructureDetailDto } from "../types";
import AppSearchSelect from "../../../../components/AppSearchSelect/AppSearchSelect";
import { getTranslatedDescription } from "../../../../utils/formatters/UserLanguageToDescriptionField";
import i18n from "../../../../i18n";
import { useUserPermissions } from "../../../../utils/auth";
import { PERMISSIONS } from "../../../../constants/permissions";

interface OrganizationalStructureDetailFormProps {
    isLoading: boolean;
    editableDetail: OrganizationalStructureDetailDto | null;
    isEditing: boolean;
    isCreating: boolean;
    isSaving: boolean;
    errors: { [key: string]: string | undefined };
    setIsEditing: (value: boolean) => void;
    setEditableDetail: React.Dispatch<React.SetStateAction<OrganizationalStructureDetailDto | null>>;
    setValidationErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string | undefined }>>;
    organizationalStructureOptions: SelectItemDto[];
    onDelete: () => void;
    onCancel: () => void;
    onSave: () => void;
}

export default function OrganizationalStructureDetailForm({
    isLoading,
    editableDetail,
    isEditing,
    isCreating,
    isSaving,
    errors,
    setIsEditing,
    setEditableDetail,
    setValidationErrors,
    organizationalStructureOptions,
    onDelete,
    onCancel,
    onSave,
}: OrganizationalStructureDetailFormProps) {
    const { t } = useTranslation(["organizationalStructure", "common"]);
    const detailName = editableDetail?.name;
    const permissions = useUserPermissions();

    return (
        <div className="border p-3 organizational-structure-panel">
            {isLoading ? (
                <div className="organizational-structure-loading">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Row>
                        <Col xs={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t("common:code")}</Form.Label>
                                <Form.Control
                                    value={editableDetail?.code || ""}
                                    readOnly={!isEditing}
                                    isInvalid={!!errors.code}
                                    onChange={(e) => {
                                        setEditableDetail((prev) =>
                                            prev ? { ...prev, code: e.target.value } : prev
                                        );
                                        setValidationErrors((prev) => ({ ...prev, code: undefined }));
                                    }}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.code}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col xs={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>{t("organizationalStructure:mainOrganizationalStructureCode")}</Form.Label>
                                {isEditing ? (
                                    <AppSearchSelect
                                        isMulti={false}
                                        value={organizationalStructureOptions.find(organizationalStructure => organizationalStructure.id == editableDetail?.parentId) ?? null}
                                        options={organizationalStructureOptions.filter(x => x.id != editableDetail?.id)}
                                        placeholder={t('common:search')}
                                        isInvalid={!!errors.parentId}
                                        disabled={editableDetail?.id != 0 && !editableDetail?.parentId}
                                        onChange={(selectedOption: SelectItemDto | SelectItemDto[] | null) => {
                                            setEditableDetail((prev) => prev ?
                                                {
                                                    ...prev,
                                                    parentId: selectedOption === null ? undefined : Number((selectedOption as SelectItemDto).id),
                                                } : prev
                                            );
                                            setValidationErrors((prev) => ({ ...prev, parentId: undefined }));
                                        }}
                                    />
                                ) : (
                                    <Form.Control value={getTranslatedDescription(editableDetail?.parentName, i18n.language) || ""} readOnly />
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>{t("organizationalStructure:nameCat")}</Form.Label>
                        <Form.Control
                            value={detailName?.catalan || ""}
                            readOnly={!isEditing}
                            isInvalid={!!errors["name.catalan"]}
                            onChange={(e) => {
                                setEditableDetail((prev) =>
                                    prev ? 
                                        {
                                            ...prev,
                                            name: {
                                                ...prev.name,
                                                catalan: e.target.value,
                                            },
                                        } : prev
                                );
                                setValidationErrors((prev) => ({ ...prev, "name.catalan": undefined }));
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors["name.catalan"]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t("organizationalStructure:nameEs")}</Form.Label>
                        <Form.Control
                            value={detailName?.spanish || ""}
                            readOnly={!isEditing}
                            isInvalid={!!errors["name.spanish"]}
                            onChange={(e) => {
                                setEditableDetail((prev) =>
                                    prev ? {
                                            ...prev,
                                            name: {
                                                ...prev.name,
                                                spanish: e.target.value,
                                            },
                                        } : prev
                                );
                                setValidationErrors((prev) => ({ ...prev, "name.spanish": undefined }));
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors["name.spanish"]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t("organizationalStructure:nameEn")}</Form.Label>
                        <Form.Control
                            value={detailName?.english || ""}
                            readOnly={!isEditing}
                            isInvalid={!!errors["name.english"]}
                            onChange={(e) => {
                                setEditableDetail((prev) =>
                                    prev ? {
                                            ...prev,
                                            name: {
                                                ...prev.name,
                                                english: e.target.value,
                                            },
                                        } : prev
                                );
                                setValidationErrors((prev) => ({ ...prev, "name.english": undefined }));
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors["name.english"]}
                        </Form.Control.Feedback>
                    </Form.Group>
                    
                    <div className="organizational-structure-actions">
                        {permissions.length > 0 && permissions.includes(PERMISSIONS.TOPIC_MANAGEMENT.DELETE) && editableDetail?.parentId && (
                            <Button
                                variant="outline-danger"
                                className="rounded-pill px-4"
                                onClick={onDelete}
                                disabled={!editableDetail || isCreating || isSaving}
                            >
                                {t("organizationalStructure:cancelOrganizationalStructure")}
                            </Button>
                        )}

                        {isEditing ? (
                            <div className="organizational-structure-actions-right">
                                <Button
                                    variant="secondary"
                                    className="rounded-pill px-4"
                                    onClick={onCancel}
                                    disabled={isSaving}
                                >
                                    {t("common:buttons.buttonCancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    className="rounded-pill px-4"
                                    onClick={onSave}
                                    disabled={!editableDetail || isSaving}
                                >
                                    {isSaving ? t("common:buttons.buttonSaveingProgress") : t("common:buttons.buttonSave")}
                                </Button>
                            </div>
                        ) : (
                            permissions.length > 0 && permissions.includes(PERMISSIONS.TOPIC_MANAGEMENT.EDIT) && (
                                <div className="organizational-structure-actions-right">
                                    <Button
                                        variant="primary"
                                        className="rounded-pill px-4"
                                        onClick={() => setIsEditing(true)}
                                        disabled={!editableDetail || isEditing || isCreating}
                                    >
                                        {t("common:buttons.buttonEdit")}
                                    </Button>
                                </div>
                            )
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
